// Párbeszédablak-keret (ds.css `.dialog`, `.scrim`, `.sheet`).
//
// Az átadási leírás akadálymentességi szabályai miatt nem elég egy abszolút
// pozicionált doboz:
//   - a fókusznak a párbeszédablakba kell kerülnie és ott is maradnia
//   - Esc zárja, a háttérre kattintás zárja
//   - bezáráskor a fókusz visszatér oda, ahonnan nyitottuk
//   - a háttér tartalma a képernyőolvasó elől el van rejtve (aria-hidden)
//
// Mobilon alsó lapként (`.sheet`) jelenik meg, ahogy a tervek mutatják.

import { t } from './i18n';
import { h } from './dom';

export type DialogHandle = {
  close: () => void;
  element: HTMLElement;
};

export type DialogOptions = {
  title: string;
  /// A tartalom; a `close` átadva, hogy a gombok be tudják zárni.
  body: (close: () => void) => HTMLElement;
  /// Lábléc-gombok jobbra igazítva.
  footer?: (close: () => void) => HTMLElement[];
  /// Szélesség desktopon.
  width?: string;
  /// A tervek egy része oldalsó fiókot mutat (W08, W17): a jobb szélhez
  /// tapadó, teljes magasságú panelt, nem középre zárt dobozt. Mobilon a
  /// kettő ugyanaz marad — alsó lap.
  placement?: 'center' | 'right';
  onClosed?: () => void;
};

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function openDialog(options: DialogOptions): DialogHandle {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const appRoot = document.querySelector<HTMLElement>('#root');

  let closed = false;

  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeyDown, true);
    overlay.remove();
    appRoot?.removeAttribute('aria-hidden');
    // A fókusz oda tér vissza, ahonnan nyitottuk — különben a billentyűzetes
    // felhasználó a lap elejére esne.
    previouslyFocused?.focus?.();
    options.onClosed?.();
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...dialog.querySelectorAll<HTMLElement>(focusableSelector)].filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    // Körbe-körbe: a fókusz nem szökhet ki a párbeszédablakból.
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const titleId = `dlg-${Math.random().toString(36).slice(2, 8)}`;

  const dialog = h(
    'div',
    {
      class: 'dialog dialog-box',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
      style:
        options.placement === 'right'
          ? {
              width: options.width ?? '400px',
              height: '100%',
              maxHeight: '100%',
              borderRadius: '0',
              borderTop: '0',
              borderRight: '0',
              borderBottom: '0',
            }
          : { width: options.width ?? '520px' },
    },
    h(
      'div',
      { class: 'dialog-head row between' },
      h('h2', { class: 'h3', id: titleId, text: options.title }),
      h('button', {
        class: 'btn btn-ghost btn-icon btn-sm',
        type: 'button',
        'aria-label': t('general.close'),
        text: '✕',
        onClick: close,
      }),
    ),
    h('div', { class: 'dialog-body' }, options.body(close)),
    ...(options.footer ? [h('div', { class: 'dialog-foot row g12' }, ...options.footer(close))] : []),
  );

  // A tokeneket (`--surface`, `--overlay`, betűcsalád) a ds.css az `.app`
  // osztályhoz köti, az pedig a `#root`-on ül. A párbeszédablak viszont a
  // `<body>` alá kerül — a `#root` alatt nem maradhat, mert nyitva
  // `aria-hidden`-t kap —, így ott EGYETLEN token sem oldódott fel: a
  // háttérfátyol és maga a doboz is átlátszó volt, a párbeszédablak szövege
  // pedig a mögötte lévő listára folyt. Ezért az `.app` (és a sötét pár) a
  // fátyolra is rákerül.
  const overlay = h(
    'div',
    {
      class: `app${appRoot?.classList.contains('dark') ? ' dark' : ''} scrim dialog-overlay${
        options.placement === 'right' ? ' dialog-overlay-right' : ''
      }`,
      onClick: (event: MouseEvent) => {
        // Csak a háttérre kattintás zár, a tartalomra nem.
        if (event.target === overlay) close();
      },
    },
    dialog,
  );

  document.body.append(overlay);
  // A háttér a képernyőolvasó elől elrejtve, amíg a párbeszédablak nyitva van.
  appRoot?.setAttribute('aria-hidden', 'true');
  document.addEventListener('keydown', onKeyDown, true);

  const firstField = dialog.querySelector<HTMLElement>(focusableSelector);
  firstField?.focus();

  return { close, element: dialog };
}

/// Megerősítő párbeszédablak visszafordíthatatlan művelethez.
export function confirmDialog(options: {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
}): DialogHandle {
  return openDialog({
    title: options.title,
    width: '440px',
    body: () => h('p', { class: 'body ts', text: options.message }),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: `btn ${options.danger ? 'btn-danger' : 'btn-primary'}`,
        type: 'button',
        text: options.confirmLabel,
        onClick: () => {
          options.onConfirm();
          close();
        },
      }),
    ],
  });
}
